import { apiFetch } from "@/api/fetch";
import { clearBetterStackUser, identifyBetterStackUser } from "@/features/analytics/utils/betterstack";
import { auth } from "@/features/auth/utils/firebase";
import {
  type GxlSignedInPayload,
  unauthenticateUser,
  useGxlFirebaseAuthSync,
} from "@gxl/web-auth";
import React, { useCallback, useState } from "react";
import { User } from "../api/users";

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  token: string | null;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
}

export const AuthContext = React.createContext<AuthContextType | null>(null);

const fetchUserFromBackend = async (): Promise<
  { status: "ok"; user: Partial<User> } | { status: "unauthorized" } | { status: "unavailable" }
> => {
  try {
    const resp = await apiFetch("/api/users/me");
    if (resp.ok) return { status: "ok", user: await resp.json() };
    if (resp.status === 401) return { status: "unauthorized" };
  } catch {
    // Best-effort — user creation and anonymous ID persistence
    // will happen on the next authenticated request if this fails.
  }
  return { status: "unavailable" };
};

/**
 * Local-only auth bypass for iterating on UI without Firebase wired up.
 * Active only under `vite dev` (never in a build) and only when
 * VITE_AUTH_BYPASS=true is set in the repo-root .env. Supplies a fake user so
 * RequireAuth passes; API calls still go out unauthenticated and will 401.
 */
const AUTH_BYPASS = import.meta.env.DEV && import.meta.env.VITE_AUTH_BYPASS === "true";
const BYPASS_USER: User = {
  id: "local-dev",
  email: "dev@localhost",
  full_name: "Local dev",
  photo_url: null,
  created_at: new Date(0).toISOString(),
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(AUTH_BYPASS ? BYPASS_USER : null);
  const [loading, setLoading] = useState(!AUTH_BYPASS);
  const [token, setToken] = useState<string | null>(AUTH_BYPASS ? "local-dev-bypass" : null);

  const onSignedIn = useCallback(async ({ firebaseUser, idToken }: GxlSignedInPayload) => {
    // Identify before /api/users/me so Better Stack associates the session early.
    identifyBetterStackUser({ id: firebaseUser.uid });
    setToken(idToken);
    const backendUser = await fetchUserFromBackend();
    if (backendUser.status === "unauthorized") {
      return;
    }
    const profile = backendUser.status === "ok" ? backendUser.user : null;
    setUser({
      id: firebaseUser.uid,
      email: firebaseUser.email || firebaseUser.uid,
      full_name: profile?.full_name || firebaseUser.displayName || (firebaseUser.email?.split("@")[0] ?? null),
      photo_url: firebaseUser.photoURL || null,
      created_at: profile?.created_at || firebaseUser.metadata.creationTime || new Date().toISOString(),
      roles: profile?.roles ?? [],
    });
    setLoading(false);
  }, []);

  const onSignedOut = useCallback(() => {
    clearBetterStackUser();
    setUser(AUTH_BYPASS ? BYPASS_USER : null);
    setToken(null);
    setLoading(false);
  }, []);

  const { firebaseUser } = useGxlFirebaseAuthSync({
    auth,
    onSignedIn,
    onSignedOut,
  });

  const logout = useCallback(async () => {
    await unauthenticateUser(auth);
  }, []);

  const deleteAccount = useCallback(async () => {
    const currentToken = await firebaseUser?.getIdToken();
    if (!currentToken) throw new Error("Not authenticated");
    const response = await fetch("/api/users/me", {
      method: "DELETE",
      headers: { Authorization: `Bearer ${currentToken}` },
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.detail || "Failed to delete account");
    }
    await unauthenticateUser(auth, "/login", currentToken, true);
  }, [firebaseUser]);

  const getIdToken = useCallback(async (forceRefresh = false): Promise<string | null> => {
    if (!firebaseUser) return null;
    return firebaseUser.getIdToken(forceRefresh);
  }, [firebaseUser]);

  const value: AuthContextType = {
    user,
    loading,
    token,
    logout,
    deleteAccount,
    getIdToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
